// Dossier magazine — FREINAGE D'URGENCE À MOTO
// Contenu original signé Bruno · Équipe Code 2 Roues · CODE 2 ROUES · 2026
import {
  Eye, Hand, AlertTriangle, Sparkles, Target, Gauge, Compass,
  Zap, ShieldCheck, TrendingUp, Route, HelpCircle, Repeat, Lightbulb, Wind,
} from 'lucide-react';

export const freinageUrgenceArticle = {
  slug: 'freinage-urgence-moto',
  weekVolume: 32,
  weekYear: 2026,
  monthLabel: 'Août 2026',
  category: 'Technique & maîtrise',
  shortTitle: 'Freinage d\'urgence — Comprendre, maîtriser',
  title: "Freinage d'urgence à moto : comprendre, maîtriser, automatiser",
  kicker: '🛑 Dossier technique · Signé Bruno · Équipe Code 2 Roues',
  subtitle:
    "Une voiture freine devant toi. Un piéton s'engage. À moto, quelques mètres font toute la différence — et il n'est plus temps de réfléchir à la technique. L'objectif n'est pas de <strong>freiner fort</strong> mais de savoir <strong>freiner fort, vite ET juste</strong>.",
  heroImage: '/images/articles/freinage-urgence-pov.png',
  heroImageAlt: 'Vue POV motard face à un chevreuil traversant la route soudainement, voiture arrivant en face — situation typique de freinage d\'urgence',
  coverPoster: '/images/articles/poster-freinage-horizontal.png',
  coverPosterAlt: "Couverture éditoriale CODE 2 ROUES — Freinage moto : 5 étapes pour freiner efficacement et en sécurité (anticipation, frein avant, frein arrière, posture, relâchement)",
  posterHorizontal: '/images/articles/poster-freinage-horizontal.png',
  posterVertical: '/images/articles/poster-freinage-vertical.png',
  posterVerticalAlt: "Poster vertical CODE 2 ROUES — Freinage à moto : 5 étapes pour freiner efficacement et en sécurité (anticipation, frein avant, frein arrière, posture, relâchement)",
  topRightKicker: { line1: 'Technique & maîtrise', line2: 'Freinage · ABS' },
  readTimeMin: 9,
  publishedAt: '2026-09-08',
  seoDescription:
    "Freinage d'urgence à moto : transfert de charge, frein avant progressif, ABS, capital adhérence, 4 exercices progressifs. Guide pédagogique complet par un moniteur ECSR.",
  seoKeywords:
    'freinage urgence moto, freinage moto, transfert de charge moto, ABS moto, distance arrêt moto, capital adhérence, frein avant moto, freinage plateau A2',
  about: "Dossier pédagogique — maîtriser le freinage d'urgence à moto pour A1/A2 et perfectionnement.",
  chapo: {
    kicker: "L'idée à retenir",
    body: "Chez CODE 2 ROUES, on parle plutôt de <strong>freinage maîtrisé à forte intensité</strong>. La qualité d'un freinage d'urgence commence AVANT d'actionner les freins — dans le regard, l'anticipation, la position.",
    sideStat: {
      kicker: "L'ordre non négociable",
      big: 'COMPRENDRE\nRESSENTIR\nMAÎTRISER\nAUTOMATISER',
      body: "La séquence pédagogique CODE 2 ROUES en 4 étapes pour transformer la théorie en réflexe.",
      accent: '#cf2031',
    },
  },
  pullQuote: {
    text: "Un excellent motard n'est pas celui qui réalise régulièrement des freinages d'urgence parfaits. C'est celui qui organise sa conduite pour avoir le moins souvent possible besoin d'en réaliser un.",
    source: 'Bruno · Équipe Code 2 Roues — Enseignant ECSR, CODE 2 ROUES',
  },
  sections: [
    {
      part: 'Partie 1 — Le contexte',
      title: 'Distance d\'arrêt : ce que la moto parcourt sans toi',
      intro: "Une hésitation d'une seconde = plusieurs dizaines de mètres parcourus avant même de commencer à freiner.",
      cards: [
        { icon: Gauge, accent: '#f59e0b', kicker: '50 km/h', title: '≈ 14 m / seconde', body: "Une seconde d'inattention = déjà 14 mètres consommés avant de commencer à freiner." },
        { icon: Gauge, accent: '#f97316', kicker: '80 km/h', title: '≈ 22 m / seconde', body: "À 80, la moto avale un couloir de circulation entier en 1 seconde d'hésitation." },
        { icon: Gauge, accent: '#cf2031', kicker: '90 km/h', title: '≈ 25 m / seconde', body: "Le meilleur freinage du monde ne récupère pas plusieurs secondes d'inattention." },
      ],
      cardsCols: 3,
      callouts: [
        {
          icon: Lightbulb,
          accent: '#0ea5e9',
          body: "<strong>Distance de réaction</strong> (perception → action) + <strong>Distance de freinage</strong> (freinage → arrêt) = <strong>Distance d'arrêt</strong>. Voilà pourquoi l'anticipation reste plus efficace que la meilleure technique de freinage.",
        },
      ],
    },
    {
      part: 'Partie 2 — La physique',
      title: 'Le transfert de charge : la clé du freinage moto',
      intro: "Quand tu freines fort, la masse (moto + toi) se transfère vers l'avant : la fourche s'enfonce, la roue avant est chargée, la roue arrière est délestée. Comprendre ce phénomène change tout.",
      compareCards: [
        {
          accent: '#16a34a',
          kicker: 'Roue AVANT',
          title: 'Puissance principale',
          intro: "Plus l'avant est chargé, plus le pneu peut transmettre d'effort de freinage — dans la limite de l'adhérence.",
          points: [
            { ok: true, text: 'Fourche enfoncée = pneu écrasé = adhérence' },
            { ok: true, text: 'Rôle majeur dans un freinage puissant' },
            { ok: false, text: 'Peur du frein avant = distance d\'arrêt allongée' },
          ],
        },
        {
          accent: '#f97316',
          kicker: 'Roue ARRIÈRE',
          title: 'Complément + stabilisation',
          intro: "L'arrière s'allège lors du transfert : son efficacité diminue. Utile pour la stabilité mais ne peut pas remplacer l'avant.",
          points: [
            { ok: true, text: 'Participe au ralentissement et à la stabilité' },
            { ok: false, text: 'Atteint plus vite sa limite d\'adhérence' },
            { ok: true, text: 'ABS intervient si blocage détecté' },
          ],
        },
      ],
    },
    {
      part: 'Partie 3 — La technique',
      title: 'Progressif ≠ Lent : les 4 phases du freinage',
      intro: "En urgence, on n'a pas plusieurs secondes pour monter la pression. La prise du frein doit être <strong>rapide</strong> — mais laisser le pneu et la suspension se mettre en charge.",
      orderedList: [
        "<strong>Prise du frein</strong> — Je mets rapidement le système en action (pas brutalement, pas hésitant).",
        "<strong>Mise en charge</strong> — Le poids se transfère vers l'avant, la fourche s'enfonce, le pneu s'écrase.",
        "<strong>Augmentation de la pression</strong> — J'exploite le potentiel de freinage maintenant que le pneu est écrasé.",
        "<strong>Réduction de la pression</strong> — À l'approche de l'arrêt et quand la vitesse chute, j'adapte pour ne pas déséquilibrer.",
      ],
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#eab308',
          body: "<strong>Oublie le « 70 % avant / 30 % arrière »</strong> comme recette universelle. La répartition varie selon la moto, la géométrie, la vitesse, l'adhérence, la pente, les pneus, l'ABS… Retiens plutôt : <em>Avant = puissance principale · Arrière = complément.</em>",
        },
      ],
    },
    {
      part: 'Partie 4 — Le corps',
      title: 'Rester pilote (pas passager projeté)',
      intro: "Freiner efficacement ne concerne pas que les mains et pieds. Ton corps participe à la maîtrise.",
      cards: [
        { icon: Eye, accent: '#0ea5e9', kicker: '👀', title: 'REGARD', body: 'Tête relevée, recherche une zone sûre. Ne fixe pas l\'obstacle.' },
        { icon: Compass, accent: '#16a34a', kicker: '🦵', title: 'JAMBES', body: 'Serrent la moto pour te maintenir. Elles portent, pas tes bras.' },
        { icon: Hand, accent: '#f97316', kicker: '💪', title: 'BRAS', body: 'Ne les transforme pas en 2 barres rigides. Souplesse = précision.' },
        { icon: Sparkles, accent: '#7c3aed', kicker: '🧍', title: 'BUSTE', body: 'Accompagne la décélération sans t\'effondrer sur le guidon.' },
      ],
      cardsCols: 4,
    },
    {
      part: 'Partie 5 — La stratégie',
      title: 'Freiner ou éviter ? Le capital adhérence',
      intro: "Ton pneu dispose d'un <strong>budget d'adhérence</strong>. Tu peux l'utiliser pour freiner OU pour tourner. Si tu freines à fond, il reste peu pour éviter.",
      cards: [
        {
          icon: Target,
          accent: '#cf2031',
          kicker: 'Stratégie A',
          title: 'Freiner',
          body: "Le freinage seul dans l'axe — utilise 100 % du budget adhérence pour ralentir. Efficace en ligne droite.",
        },
        {
          icon: Route,
          accent: '#0ea5e9',
          kicker: 'Stratégie B',
          title: 'Éviter',
          body: "L'évitement seul — utilise le budget pour changer de trajectoire. Nécessite de conserver de la marge d'adhérence.",
        },
        {
          icon: Zap,
          accent: '#7c3aed',
          kicker: 'Stratégie C',
          title: 'Freiner PUIS éviter',
          body: "La combinaison intelligente : <em>freiner fort → réduire le freinage → éviter</em>. Pas simultané. Décomposé.",
        },
      ],
      cardsCols: 3,
      callouts: [
        {
          icon: HelpCircle,
          accent: '#f59e0b',
          body: "<strong>La bonne question :</strong> pas seulement « Comment vais-je m'arrêter ? » mais aussi <em>« Où puis-je aller si je ne peux pas m'arrêter ? »</em> — garde mentalement une porte de sortie.",
        },
      ],
    },
    {
      part: 'Partie 6 — L\'ABS',
      title: "L'ABS : ce qu'il fait vraiment (et ce qu'il ne fait pas)",
      intro: "ABS = Antiblockiersystem (système antiblocage). Objectif : éviter qu'une roue reste bloquée pendant un freinage. Les capteurs modulent automatiquement la pression.",
      compareCards: [
        {
          accent: '#16a34a',
          kicker: 'Ce que fait l\'ABS',
          title: 'Une aide majeure',
          intro: "Comment réagir quand l'ABS intervient :",
          points: [
            { ok: true, text: 'Applique un freinage fort et maîtrisé' },
            { ok: true, text: 'Conserve ton action même si tu sens les pulsations' },
            { ok: true, text: 'Garde la moto stable, regarde ta zone de sécurité' },
            { ok: false, text: 'NE relâche PAS parce que tu sens les vibrations' },
          ],
        },
        {
          accent: '#cf2031',
          kicker: 'Ce que l\'ABS NE fait PAS',
          title: 'Il ne supprime pas la physique',
          intro: "Il ne peut pas créer de l'adhérence là où il n'y en a plus. Ta distance dépend toujours de :",
          points: [
            { ok: false, text: 'Pluie, revêtement, pente' },
            { ok: false, text: 'Pneus, chargement, moto' },
            { ok: false, text: 'Vitesse (doublée = distance × 4 !)' },
            { ok: false, text: 'ABS classique ≠ ABS sensible à l\'inclinaison' },
          ],
        },
      ],
    },
    {
      part: 'Partie 7 — La pratique',
      title: '4 exercices progressifs',
      intro: "Progression pédagogique CODE 2 ROUES. Chaque exercice construit le suivant — ne saute pas d'étape.",
      orderedList: [
        "<strong>Exercice 1 — Découvrir son frein avant.</strong> Faible allure, trajectoire droite. Stabilise, regarde loin, prends progressivement le frein avant. Ressens l'enfoncement de la fourche. Objectif : comprendre le transfert de charge, pas chercher la performance.",
        "<strong>Exercice 2 — Construire un freinage complet.</strong> Utilise les freins de manière coordonnée. Stabilise ton corps avec les jambes. Regard relevé. Augmente progressivement l'intensité. Place un repère de début de freinage et observe où tu t'arrêtes. On cherche la <strong>régularité, pas le record.</strong>",
        "<strong>Exercice 3 — Augmenter progressivement l'intensité.</strong> Quand la technique devient propre, augmente la vitesse d'approche. Retrouve à chaque passage : <em>Regard → Position → Prise des freins → Mise en charge → Freinage → Arrêt</em>. Crée une procédure reproductible.",
        "<strong>Exercice 4 — Découvrir l'ABS.</strong> Dans un environnement sécurisé, ton moniteur peut organiser un exercice pour te faire ressentir l'intervention de l'ABS. Découvrir les vibrations pour la 1ʳᵉ fois le jour où une voiture te coupe la route n'est pas idéal. Cette connaissance réduit l'effet de surprise.",
      ],
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body: "<strong>Ce qu'on NE conseille PAS à un débutant :</strong> provoquer volontairement le blocage d'une roue avec l'ABS désactivé. Chez CODE 2 ROUES, priorité à la séquence <em>Comprendre → Maîtriser → Automatiser</em> avant de chercher les limites d'adhérence.",
        },
      ],
    },
    {
      part: 'À retenir',
      title: 'Les 8 réflexes du freinage d\'urgence',
      cards: [
        { icon: Eye, accent: '#0ea5e9', kicker: '👀', title: 'REGARD', body: 'Je regarde loin, je cherche une solution.' },
        { icon: Compass, accent: '#16a34a', kicker: '🧍', title: 'POSITION', body: 'Je stabilise mon corps sans me verrouiller.' },
        { icon: Hand, accent: '#cf2031', kicker: '🛑', title: 'FREIN AVANT', body: 'J\'exploite progressivement sa puissance.' },
        { icon: Wind, accent: '#f97316', kicker: '🦶', title: 'FREIN ARRIÈRE', body: 'En complément selon la situation.' },
        { icon: Target, accent: '#7c3aed', kicker: '🛞', title: 'ADHÉRENCE', body: 'Mes pneus ont une limite. Je respecte le budget.' },
        { icon: ShieldCheck, accent: '#16a34a', kicker: '⚙️', title: 'ABS', body: 'Je connais son fonctionnement, je ne panique pas.' },
        { icon: Route, accent: '#eab308', kicker: '↪️', title: 'ÉVITEMENT', body: 'Je garde mentalement une porte de sortie.' },
        { icon: TrendingUp, accent: '#16a34a', kicker: '🧠', title: 'ANTICIPATION', body: 'Éviter le besoin est mieux que réussir le freinage.' },
      ],
      cardsCols: 4,
    },
  ],
  ctaFinal: {
    kicker: 'Pratique guidée',
    title: 'Enchaîne avec le livret plateau interactif',
    body: "L'exercice « Freinage d'urgence » fait partie des <strong>6 manœuvres officielles</strong> du plateau A2 (M3). Retrouve-le détaillé étape par étape dans le livret, avec cases à cocher et PDF imprimable.",
    primary: { label: 'Ouvrir le livret Plateau', to: '/livret-plateau' },
    secondary: { label: 'Voir la fiche plateau complète', to: '/epreuves/plateau' },
  },
};
